import pluralize from 'pluralize';

import CART_ITEM from '../constants/cartItems';
import { getDigitalDownloadPhotoPrice } from '../../../apps/pro/views/HierarchyItem/Matches/components/PhotoPurchasingOptions/helpers';
import { CURRENCY } from '../../../constants/currency';
import { CUSTOM_PRINT_SUBTYPE } from '../../../constants/pro-custom-prints';

/**
 * Returns a cart item searching for it by AlbumFolder id and Identity id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.albumFolderId The AlbumFolder id.
 * @param {string} opts.identityId The Identity id.
 * @returns {Object|undefined}
 */
export const getAlbumFolderSubscriptionCartItem = (
  cart = [],
  { albumFolderId, identityId },
) => {
  if (!cart || !cart.length || !albumFolderId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION &&
      item.albumFolder.id === albumFolderId &&
      item.identity.id === identityId,
  );
};

/**
 * Returns a cart item searching for it by Album id and Identity id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.albumId The Album id.
 * @param {string} opts.identityId The Identity id.
 * @returns {Object|undefined}
 */
export const getAlbumSubscriptionCartItem = (
  cart = [],
  { albumId, identityId },
) => {
  if (!cart || !cart.length || !albumId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.ALBUM_SUBSCRIPTION &&
      item.album.id === albumId &&
      item.identity.id === identityId,
  );
};

/**
 * Returns a cart item searching for it by AlbumCustomPrint ID.
 *
 * When used with identityId this method should be used to check whether an albumCustomPrint tied to a certain identity exists
 * When used with accountCardId, this method should be used when multiple customPrints are supported by identity.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.albumCustomPrintId The AlbumCustomPrint id.
 * @param {string} opts.identityId The Identity id.
 * @param {string} opts.accountCardId The account card id.
 * @returns {Object|undefined}
 */
export const getAlbumCustomPrintCartItem = (
  cart = [],
  { albumCustomPrintId, identityId, accountCardId },
) => {
  if (!cart || !cart.length || !albumCustomPrintId) {
    return undefined;
  }

  // Force the check for accountCardId when available.
  if (accountCardId) {
    return cart.find(
      item =>
        item.type === CART_ITEM.ALBUM_CUSTOM_PRINT &&
        item.albumCustomPrint.id === albumCustomPrintId &&
        accountCardId &&
        item.configuration?.accountCard?.id === accountCardId,
    );
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.ALBUM_CUSTOM_PRINT &&
      item.albumCustomPrint.id === albumCustomPrintId &&
      identityId &&
      item.identity?.id === identityId,
  );
};

/**
 * Returns a cart item searching for it by AlbumPrintPackage id and Photo id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.albumPrintPackageId The AlbumPrintPackage id.
 * @param {string} opts.photoId The Photo id.
 * @param {string} opts.identityId The Identity's id.
 * @returns {Object|undefined}
 */
export const getAlbumPrintPackageCartItem = (
  cart = [],
  { albumPrintPackageId, photoId, identityId },
) => {
  if (!cart || !cart.length || !albumPrintPackageId) {
    return undefined;
  }

  return cart.find(
    item =>
      !item?.ignoreOnPurchasingOptions &&
      item.type === CART_ITEM.ALBUM_PRINT_PACKAGE &&
      item.albumPrintPackage.id === albumPrintPackageId &&
      ((photoId && item.photo?.id === photoId) ||
        (item.identity?.id === identityId && item.prePaid)),
  );
};

/**
 * Returns a cart item searching for it by PurchasedPrintPackage id and Photo id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.purchasedPrintPackageId The PurchasedPrintPackage id.
 * @param {string} opts.photoId The Photo id.
 * @param {string} opts.identityId The Identity's id.
 * @returns {Object|undefined}
 */
export const getPurchasedPrintPackageCartItem = (
  cart = [],
  { purchasedPrintPackageId, photoId, identityId },
) => {
  if (!cart || !cart.length || !purchasedPrintPackageId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PURCHASED_PRINT_PACKAGE &&
      item.purchasedPrintPackage.id === purchasedPrintPackageId &&
      ((photoId && item.photo?.id === photoId) ||
        item.identity?.id === identityId),
  );
};

/**
 * Returns a cart item searching for it by PurchasedDigitalDownload id and Photo id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.purchasedPhotoPricingId The PurchasedDigitalDownload id.
 * @param {string} opts.photoId The photo id.
 * @returns {Object|undefined}
 */
export const getPurchasedDigitalDownloadCartItem = (
  cart = [],
  { purchasedPhotoPricingId, photoId },
) => {
  if (!cart || !cart.length || !purchasedPhotoPricingId) {
    return undefined;
  }

  if (photoId) {
    return cart.find(
      item =>
        item.type === CART_ITEM.PURCHASED_DIGITAL_DOWNLOAD &&
        item.purchasedPhotoPricing.id === purchasedPhotoPricingId &&
        photoId === item.photo?.id,
    );
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PURCHASED_DIGITAL_DOWNLOAD &&
      item.purchasedPhotoPricing.id === purchasedPhotoPricingId,
  );
};

/**
 * Returns a cart item searching for it by purchasedPhotoPricingPrintProductItem id and Photo id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.purchasedPhotoPricingId The purchasedPhotoPricingPrintProductItem id.
 * @returns {Object|undefined}
 */
export const getPurchasedPhotoPricingPrintProductCartItem = (
  cart = [],
  { purchasedPhotoPricingId },
) => {
  if (!cart || !cart.length || !purchasedPhotoPricingId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PURCHASED_PHOTO_PRICING_PRINT_PRODUCT &&
      item.purchasedPhotoPricing.id === purchasedPhotoPricingId,
  );
};

/**
 * Returns a cart item searching for it by purchasedPhotoPricingPrintProductItem id and Photo id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.purchasedCustomPrintId The purchasedPhotoPricingPrintProductItem id.
 * @returns {Object|undefined}
 */
export const getPurchasedCustomPrintCartItem = (
  cart = [],
  { purchasedCustomPrintId },
) => {
  if (!cart || !cart.length || !purchasedCustomPrintId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PURCHASED_CUSTOM_PRINT &&
      item.purchasedCustomPrint.id === purchasedCustomPrintId,
  );
};

/**
 * Returns a digital download cart item searching for it by photo id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.photoId The Photo id.
 * @param {string} opts.identityId The Identity id.
 * @param {boolean} opts.prePaid Wether or not this is pre-paid
 *
 * @returns {Object|undefined}
 */
export const getDigitalDownloadCartItem = (
  cart = [],
  { photoId, identityId, prePaid },
) => {
  if (!cart.length || (!photoId && !prePaid) || (!identityId && prePaid)) {
    return undefined;
  }

  if (prePaid) {
    return cart.find(
      item =>
        item.prePaid &&
        item.type === CART_ITEM.DIGITAL_DOWNLOAD &&
        item.identity?.id === identityId,
    );
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.DIGITAL_DOWNLOAD && item.photo.id === photoId,
  );
};

/**
 * Returns an individual print cart item searching for it by photo id and print item id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.photoId The Photo id.
 * @param {string} opts.printItemId The PrintItem id.
 * @returns {Object|undefined}
 */
export const getIndividualPrintCartItem = (
  cart = [],
  { photoId, printItemId },
) => {
  if (!cart.length || !photoId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.INDIVIDUAL_PRINT &&
      item.photo.id === photoId &&
      item.printItem.id === printItemId,
  );
};

/**
 * Returns a legacy print cart item searching for it by photo id and photo print size id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.photoId The Photo id.
 * @param {string} opts.photoPrintSizeId The PhotoPrintSize id.
 * @returns {Object|undefined}
 */
export const getLegacyPrintCartItem = (
  cart = [],
  { photoId, photoPrintSizeId },
) => {
  if (!cart.length || !photoId || !photoPrintSizeId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.LEGACY_PRINT &&
      item.photo.id === photoId &&
      item.photoPrintSize.id === photoPrintSizeId,
  );
};

/**
 * Returns a photo download cart item searching for it by photo id and photo print size id.
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.photoId The Photo id.
 * @param {string} opts.albumId The Album id.
 * @returns {Object|undefined}
 */
export const getPhotoDownloadCartItem = (cart = [], { photoId, albumId }) => {
  if (!cart.length || !photoId || !albumId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PHOTO_DOWNLOAD &&
      item.photo.id === photoId &&
      item.album.id === albumId,
  );
};

/**
 * Returns a PrintProduct cart item..
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.printProductSizeId The PrintProductSize id
 * @param {string} opts.photoId The photo id
 * @returns {Object|undefined}
 */
export const getPrintProductCartItem = (
  cart = [],
  { printProductSizeId, photoId },
) => {
  if (!cart.length || !printProductSizeId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PRINT_PRODUCT &&
      item.printProductSize.id === printProductSizeId &&
      item.photo?.id === photoId,
  );
};

/**
 * Returns an photo pricing print package cart item searching for it by photoPricingPrintPackageId
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.photoPricingPrintProductId The Photo Pricing Print Product Id.
 * @param {string} opts.photoId The Photo Id.
 * @param {String} opts.identityId The identity id
 * @param {String} opts.prePaid whether or not this is a pre-paid item
 * @returns {Object|undefined}
 */
export const getPhotoPricingPrintProduct = (
  cart = [],
  { photoPricingPrintProductId, photoId, identityId, prePaid },
) => {
  if (!cart.length || !photoPricingPrintProductId || (!identityId && prePaid)) {
    return undefined;
  }

  if (prePaid) {
    return cart.find(
      item =>
        item.prePaid &&
        item.type === CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT &&
        item.photoPricingPrintProduct.id === photoPricingPrintProductId &&
        item.identity?.id === identityId,
    );
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT &&
      item.photoPricingPrintProduct.id === photoPricingPrintProductId &&
      item.photo.id === photoId,
  );
};

/**
 * Returns an digital bundle cart item searching for it by digitalBundleId
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.digitalBundleId The Digital Bundle Id.
 * @param {String} opts.identityId The identity id
 * @param {String} opts.prePaid whether or not this is a pre-paid item
 * @param {String} opts.photoId the photo id
 * @param {Number} opts.photosCount the photo count for a given digital bundle
 * @returns {Object|undefined}
 */
export const getDigitalBundleCartItem = (
  cart = [],
  { digitalBundleId, identityId, photosCount, photoId, prePaid },
) => {
  if (!cart.length || !digitalBundleId || !identityId) {
    return undefined;
  }

  if (prePaid) {
    return cart.find(
      item =>
        item.prePaid &&
        item.type === CART_ITEM.DIGITAL_BUNDLE &&
        item.digitalBundle.id === digitalBundleId &&
        item.identity?.id === identityId,
    );
  }

  if (photosCount === 1 && photoId) {
    return cart.find(
      item =>
        item.type === CART_ITEM.DIGITAL_BUNDLE &&
        item.digitalBundle.id === digitalBundleId &&
        item.photos?.[0]?.id === photoId &&
        item.identity?.id === identityId,
    );
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.DIGITAL_BUNDLE &&
      item.digitalBundle.id === digitalBundleId &&
      item.identity?.id === identityId,
  );
};

/**
 * Returns a print order service cart item searching for it by printOrderServiceId
 *
 * @param {Array} cart The cart.
 * @param {Object} opts The options.
 * @param {string} opts.printPartnerOrderServiceId The Print Partner Order Service Id.
 * @returns {Object|undefined}
 */
export const getPrintPartnerOrderServiceCartItem = (
  cart = [],
  { printPartnerOrderServiceId },
) => {
  if (!cart.length || !printPartnerOrderServiceId) {
    return undefined;
  }

  return cart.find(
    item =>
      item.type === CART_ITEM.PRINT_ORDER_SERVICE &&
      item.printPartnerOrderService.id === printPartnerOrderServiceId,
  );
};

/**
 * Checks whether a cart item (or potentially cart item) exists
 * in the cart.
 *
 * @param {Array} cart The cart.
 * @param {Object} item The cart item.
 * @returns {boolean}
 */
export const cartItemExists = (cart, item) => {
  if (!item) {
    return false;
  }

  if (item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION) {
    return !!getAlbumFolderSubscriptionCartItem(cart, {
      albumFolderId: item.albumFolder.id,
      identityId: item.identity.id,
    });
  }

  if (item.type === CART_ITEM.ALBUM_SUBSCRIPTION) {
    return !!getAlbumSubscriptionCartItem(cart, {
      albumId: item.album.id,
      identityId: item.identity.id,
    });
  }

  if (item.type === CART_ITEM.ALBUM_CUSTOM_PRINT) {
    return !!getAlbumCustomPrintCartItem(cart, {
      albumCustomPrintId: item.albumCustomPrint.id,
      identityId: item.identity?.id,
      accountCardId: item.configuration?.accountCard?.id,
    });
  }

  if (item.type === CART_ITEM.ALBUM_PRINT_PACKAGE) {
    return !!getAlbumPrintPackageCartItem(cart, {
      albumPrintPackageId: item.albumPrintPackage.id,
      photoId: item.photo?.id,
      identityId: item.identity?.id,
    });
  }

  if (item.type === CART_ITEM.DIGITAL_DOWNLOAD) {
    return !!getDigitalDownloadCartItem(cart, {
      photoId: item.photo?.id,
      identityId: item.identity?.id,
      prePaid: item.prePaid,
    });
  }

  if (item.type === CART_ITEM.INDIVIDUAL_PRINT) {
    return !!getIndividualPrintCartItem(cart, {
      photoId: item.photo.id,
      printItemId: item.printItem.id,
    });
  }

  if (item.type === CART_ITEM.LEGACY_PRINT) {
    return !!getLegacyPrintCartItem(cart, {
      photoId: item.photo.id,
      photoPrintSizeId: item.photoPrintSize.id,
    });
  }

  if (item.type === CART_ITEM.PRINT_PRODUCT) {
    return !!getPrintProductCartItem(cart, {
      photoId: item.photo?.id,
      printProductSizeId: item.printProductSize.id,
    });
  }

  return false;
};

/**
 * Takes the matched photos cart and groups all cart items by identity.
 *
 * @param {Array} cart The cart.
 * @returns {Array<Object>} The cart grouped by identity.
 */
export const groupCartItemsByIdentity = (cart = []) => {
  return cart.reduce((identityRows, item) => {
    const identityRow = identityRows.find(
      row => row.identity?.id === item.identity.id,
    );

    if (identityRow) {
      identityRow.items = [item, ...identityRow.items];

      return identityRows;
    }

    identityRows.push({
      identity: item.identity,
      items: [item],
    });

    return identityRows;
  }, []);
};

//
// Client-side pricing
//

/**
 * Returns an AlbumFolder subscription cart item price.
 *
 * @param {Object} item The AlbumFolder subscription cart item.
 * @param {Object} item.albumFolder The AlbumFolder.
 * @returns {{value, currency}}
 */
export const getAlbumFolderSubscriptionCartItemPrice = item => {
  const { albumFolder } = item;

  if (albumFolder.limited_time_offer_active) {
    return albumFolder.limited_time_offer_price || albumFolder.price;
  }

  return albumFolder.price;
};

/**
 * Returns an Album subscription cart item price.
 *
 * @param {Object} item The Album subscription cart item.
 * @param {Object} item.album The Album.
 * @returns {{value, currency}}
 */
export const getAlbumSubscriptionCartItemPrice = item => {
  const { album } = item;

  return album.price_whole_album;
};

/**
 * Returns an AlbumCustomPrint cart item price.
 *
 * @param {Object} item The AlbumCustomPrint cart item.
 * @param {Object} item.albumCustomPrint The AlbumCustomPrint.
 * @returns {{value, currency}}
 */
export const getAlbumCustomPrintCartItemPrice = item => {
  const { albumCustomPrint, quantity = 1, photo } = item;
  const pricedTag = photo?.price_album_folder_tag;
  const { price, custom_print: customPrint } = albumCustomPrint;

  if (
    pricedTag &&
    customPrint?.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
  ) {
    const relatedPricedTagCustomPrint = (
      pricedTag?.custom_print_album_folder_tags?.records ?? []
    ).find(
      customPrintAlbumFolderTag =>
        customPrintAlbumFolderTag.custom_print.id === customPrint.id,
    );

    // Safeguard against a 0 value or erroneous setup.
    if (
      relatedPricedTagCustomPrint &&
      relatedPricedTagCustomPrint.price?.value
    ) {
      return {
        value: relatedPricedTagCustomPrint.price.value * item.quantity,
        currency: relatedPricedTagCustomPrint.price.currency,
      };
    }
  }

  // TODO [cart] For this to be totally accurate we'd need to check
  // whether the item.identity here also has any kind of subscription in their cart and
  // if so apply the discount price. (so it would need two params here, cart as well)
  if (item.identityIsSubscribed) {
    const finalPrice = albumCustomPrint.discount_price ?? price;
    return {
      value: finalPrice.value * quantity,
      currency: finalPrice.currency,
    };
  }

  return {
    value: price.value * quantity,
    currency: price.currency,
  };
};

/**
 * Returns an AlbumPrintPackage cart item price.
 *
 * @param {Object} item The AlbumPrintPackage cart item.
 * @param {Object} item.albumPrintPackage The AlbumPrintPackage.
 * @returns {{value, currency}}
 */
export const getAlbumPrintPackageCartItemPrice = item => {
  const { albumPrintPackage, includeDigitalDownload, quantity } = item;
  const {
    price,
    digital_downloads_upsell_price: digitalUpsellPrice,
  } = albumPrintPackage.print_package;

  return {
    value:
      price.value * quantity +
      (includeDigitalDownload ? digitalUpsellPrice.value : 0),
    currency: price.currency,
  };
};

/**
 * Returns a Digital Download cart item price.
 *
 * @param {Object} item The digital download cart item.
 * @returns {{value, currency}}
 */
export const getDigitalDownloadCartItemPrice = item => {
  return getDigitalDownloadPhotoPrice(item.photo, item.photoPricing);
};

/**
 * Returns an Individual Print cart item price.
 *
 * @param {Object} item The individual print cart item.
 * @returns {{value, currency}}
 */
export const getIndividualPrintCartItemPrice = item => {
  return {
    value: item.printItem.price.value * item.quantity,
    currency: item.printItem.price.currency,
  };
};

/**
 * Returns a Legacy Print cart item price.
 *
 * @param {Object} item The legacy print cart item.
 * @returns {{value, currency}}
 */
export const getLegacyPrintCartItemPrice = item => {
  return {
    value: item.photoPrintSize.price.value * item.quantity,
    currency: item.photoPrintSize.price.currency,
  };
};

/**
 * Returns a Photo Download cart item price based off FWG Pricing.
 *
 * @param {Object} item The digital download cart item.
 * @returns {{value, currency}}
 */
export const getPhotoDownloadCartItemPrice = item => {
  return item.webGalleryPrice;
};

/**
 * Returns a PrintProduct cart item price.
 *
 * @param {Object} item The PrintProduct cart item.
 * @returns {{value, currency}}
 */
export const getPrintProductCartItemPrice = item => {
  const price = item.accountCard.print_product_configuration.total_price;

  return {
    ...price,
    value: price.value * (item.quantity ?? 1),
  };
};

/**
 * Returns a Photo Pricing Print Product cart item price.
 *
 * @param {Object} item The Photo Pricing Print Product print cart item.
 * @returns {{value, currency}}
 */
export const getPhotoPricingPrintProductCartItemPrice = item => {
  const pricedTag = item.photo?.price_album_folder_tag;
  if (pricedTag && item.photoPricingPrintProduct) {
    const relatedPricedTagPrintProduct = (
      pricedTag.print_product_items?.records ?? []
    ).find(
      printProductItem =>
        printProductItem.print_product_size.id ===
        item.photoPricingPrintProduct.print_product_size.id,
    );

    // Safeguard against a 0 value or erroneous setup.
    if (
      relatedPricedTagPrintProduct &&
      relatedPricedTagPrintProduct.price?.value
    ) {
      return {
        value: relatedPricedTagPrintProduct.price.value * item.quantity,
        currency: relatedPricedTagPrintProduct.price.currency,
      };
    }
  }

  return {
    value: item.photoPricingPrintProduct.price.value * item.quantity,
    currency: item.photoPricingPrintProduct.price.currency,
  };
};

/**
 * Returns a Digital Bundle cart item price.
 *
 * @param {Object} item The Photo Pricing Print Product print cart item.
 * @returns {{value, currency}}
 */
export const getDigitalBundleCartItemPrice = item => {
  const { digitalBundle, quantity } = item;

  return {
    ...digitalBundle.price,
    value: digitalBundle.price.value * (quantity ?? 1),
  };
};

/**
 * Returns a cart item's price.
 *
 * @param {Object} item The cart item.
 * @returns {undefined|{value, currency}}
 */
export const getCheckoutCartItemPrice = item => {
  if (item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION) {
    return getAlbumFolderSubscriptionCartItemPrice(item);
  }

  if (item.type === CART_ITEM.ALBUM_SUBSCRIPTION) {
    return getAlbumSubscriptionCartItemPrice(item);
  }

  if (item.type === CART_ITEM.ALBUM_CUSTOM_PRINT) {
    return getAlbumCustomPrintCartItemPrice(item);
  }

  if (item.type === CART_ITEM.ALBUM_PRINT_PACKAGE) {
    return getAlbumPrintPackageCartItemPrice(item);
  }

  if (
    [
      CART_ITEM.PURCHASED_PRINT_PACKAGE,
      CART_ITEM.PURCHASED_DIGITAL_DOWNLOAD,
      CART_ITEM.PURCHASED_PHOTO_PRICING_PRINT_PRODUCT,
      CART_ITEM.PURCHASED_CUSTOM_PRINT,
    ].includes(item.type)
  ) {
    return {
      value: 0,
      currency: CURRENCY.USD,
    };
  }

  if (item.type === CART_ITEM.DIGITAL_DOWNLOAD) {
    return getDigitalDownloadCartItemPrice(item);
  }
  if (item.type === CART_ITEM.PHOTO_DOWNLOAD) {
    return getPhotoDownloadCartItemPrice(item);
  }

  if (item.type === CART_ITEM.INDIVIDUAL_PRINT) {
    return getIndividualPrintCartItemPrice(item);
  }

  if (item.type === CART_ITEM.LEGACY_PRINT) {
    return getLegacyPrintCartItemPrice(item);
  }

  if (item.type === CART_ITEM.PRINT_PRODUCT) {
    return getPrintProductCartItemPrice(item);
  }

  if (item.type === CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT) {
    return getPhotoPricingPrintProductCartItemPrice(item);
  }

  if (item.type === CART_ITEM.DIGITAL_BUNDLE) {
    return getDigitalBundleCartItemPrice(item);
  }

  if (item.type === CART_ITEM.PRINT_ORDER_SERVICE) {
    return item.printPartnerOrderService.price;
  }

  return undefined;
};

/**
 * Returns a cart item's name.
 *
 * @param {Object} item The cart item's name.
 * @returns {undefined|String}
 */
export const getCheckoutCartItemName = item => {
  if (item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION) {
    return 'Digital Photo Pass';
  }

  if (item.type === CART_ITEM.ALBUM_SUBSCRIPTION) {
    return `Digital Photo Pass - ${item.album.name}`;
  }

  if (item.type === CART_ITEM.ALBUM_CUSTOM_PRINT) {
    return (
      item.albumCustomPrint?.custom_print?.name ??
      pluralize('Custom Print', item.quantity)
    );
  }

  if (item.type === CART_ITEM.ALBUM_PRINT_PACKAGE) {
    return (
      item.albumPrintPackage?.print_package?.name ??
      pluralize('Print Package', item.quantity)
    );
  }

  if (item.type === CART_ITEM.DIGITAL_DOWNLOAD) {
    return pluralize('Digital Photo Download', item.quantity);
  }
  if (item.type === CART_ITEM.PHOTO_DOWNLOAD) {
    return pluralize('Hi-Res Download', item.quantity);
  }

  if (item.type === CART_ITEM.INDIVIDUAL_PRINT) {
    return pluralize(`${item.printItem?.type?.name} Print`, item.quantity);
  }

  if (item.type === CART_ITEM.LEGACY_PRINT) {
    return pluralize(`${item.photoPrintSize?.name} Print`, item.quantity);
  }

  if (item.type === CART_ITEM.PRINT_PRODUCT) {
    return (
      item.accountCard?.print_product_configuration?.name ??
      pluralize('Product', item.quantity)
    );
  }

  if (item.type === CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT) {
    return (
      item.photoPricingPrintProduct?.name ?? pluralize('Product', item.quantity)
    );
  }

  return '';
};

/**
 * Determines whether an AlbumPrintPackage requires shipping.
 *
 * Only AlbumPrintPackages with print items require shipping, it is
 * theoretically possible (but not adviseable) to set up a print package
 * having only a digital download.
 *
 * @param {Object} albumPrintPackage The AlbumPrintPackage.
 * @returns {boolean}
 */
export const albumPrintPackageRequiresShipping = albumPrintPackage => {
  if (!albumPrintPackage) {
    return false;
  }

  return albumPrintPackage.print_package.items.records.length > 0;
};

export const getParsedRoesConfiguration = roesConfiguration => {
  const parsedConfig = {
    image_node_configurations: [],
    text_node_configurations: [],
  };
  Object.keys(roesConfiguration).forEach(key => {
    if (typeof roesConfiguration[key] === 'string') {
      parsedConfig.text_node_configurations.push({
        node_id: key,
        text: roesConfiguration[key],
      });
    } else if (roesConfiguration[key].thumbnail_urls) {
      parsedConfig.image_node_configurations.push({
        node_id: key,
        photo_id: roesConfiguration[key].id,
      });
    }
  });

  return parsedConfig;
};
